import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { toast as islandToast, type ToastType } from '../../lib/toast'
import { DEALS_CHANGED } from '../shared/events'

/**
 * Data hook for the Deals board. Mirrors legacy `loadDeals()` and `onDrop()`
 * in crm/index.html: same select columns, same `user_id` scope (own rows
 * plus legacy rows with a NULL user_id), same newest-first order, and the
 * same stage write (`stage` + `updated_at`, optimistic, reverted on error).
 *
 * Add / edit / delete stay in the legacy modal and drawer. Those handlers
 * call `loadDeals()`, which dispatches `deals:changed` on the islands bus
 * while this island is mounted; the hook re-fetches on that event.
 */

export const DEALS_SELECT =
  'id,title,stage,value,close_date,probability,contact_id,account_id,notes,created_at,' +
  'contacts(full_name,first_name,last_name,company_name,company_domain),' +
  'accounts(company_name,company_domain)'

export interface PipelineDeal {
  id: string
  title: string | null
  stage: string
  value: number | string | null
  close_date: string | null
  probability: number | null
  contact_id: string | null
  account_id: string | null
  notes: string | null
  created_at: string | null
  contacts: {
    full_name: string | null
    first_name: string | null
    last_name: string | null
    company_name: string | null
    company_domain: string | null
  } | null
  accounts: { company_name: string | null; company_domain: string | null } | null
}

export interface UsePipelineResult {
  deals: PipelineDeal[]
  status: 'idle' | 'loading' | 'success' | 'error'
  error: string | null
  refetch: () => void
  /** Moves a deal to `stage`. Resolves true when the write succeeded. */
  moveDeal: (id: string, stage: string) => Promise<boolean>
}

type HostWindow = Window & {
  toast?: (msg: string, type?: string) => void
  refreshHomeIfVisible?: () => void
}

/**
 * Show a toast through the host's `toast()` (the visible one); fall back to
 * the island toast service when the host isn't there (tests, standalone).
 */
export function notify(msg: string, type: ToastType = ''): void {
  const hostToast = (window as HostWindow).toast
  if (typeof hostToast === 'function') hostToast(msg, type)
  else islandToast(msg, type)
}

export function usePipeline(userId: string | null): UsePipelineResult {
  const [deals, setDeals] = useState<PipelineDeal[]>([])
  const [status, setStatus] = useState<UsePipelineResult['status']>('idle')
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const dealsRef = useRef<PipelineDeal[]>([])
  dealsRef.current = deals

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), [])

  useEffect(() => {
    const bus = window.ShaunIslands?.events
    if (!bus) return
    const handler = () => refetch()
    bus.addEventListener(DEALS_CHANGED, handler)
    return () => bus.removeEventListener(DEALS_CHANGED, handler)
  }, [refetch])

  useEffect(() => {
    if (!userId) {
      setStatus('error')
      setError('Not signed in')
      setDeals([])
      return
    }
    let cancelled = false
    // Keep the board on screen during a background re-fetch; only show the
    // loading state on the first load.
    if (dealsRef.current.length === 0) setStatus('loading')
    setError(null)
    void (async () => {
      try {
        const { data, error: queryError } = await supabase
          .from('deals')
          .select(DEALS_SELECT)
          .or(`user_id.eq.${userId},user_id.is.null`)
          .order('created_at', { ascending: false })
        if (cancelled) return
        if (queryError) {
          setStatus('error')
          setError(queryError.message)
          notify('Failed to load deals: ' + queryError.message, 'error')
          return
        }
        setDeals((data ?? []) as unknown as PipelineDeal[])
        setStatus('success')
      } catch (e) {
        if (cancelled) return
        setStatus('error')
        setError(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId, refreshKey])

  const moveDeal = useCallback(async (id: string, stage: string): Promise<boolean> => {
    const deal = dealsRef.current.find((d) => d.id === id)
    if (!deal || deal.stage === stage) return false
    const prevStage = deal.stage
    const setStage = (s: string) =>
      setDeals((ds) => ds.map((d) => (d.id === id ? { ...d, stage: s } : d)))

    setStage(stage)
    const { error: updateError } = await supabase
      .from('deals')
      .update({ stage, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (updateError) {
      setStage(prevStage)
      notify('Stage update failed', 'error')
      return false
    }
    notify(`Moved to ${stage}`, 'success')
    ;(window as HostWindow).refreshHomeIfVisible?.()
    return true
  }, [])

  return { deals, status, error, refetch, moveDeal }
}
